import {
  get,
  find,
  cloneDeep,
  isUndefined,
  isEmpty,
  isBoolean,
  flatMapDeep,
  unionBy,
} from 'lodash';
import { v1 as uuid } from 'uuid';

import {
  replaceMentionedPromptsWithBlank,
  wrapMentionsWithSpan,
  getPromptFromHtml,
  hasPiping,
} from 'components/FormattedInput/helpers';

import { hasOtherAnswer } from 'containers/QuestionnairePage/GenericQuestion/QuestionFooter/utils';
import { isLoopAndMergeAttributeBasedOnQuestion } from 'containers/QuestionnairePage/QuestionGroup/utils';
import {
  getLoopAndMergeGroupWithMergedNode,
  getBipolarAnswerBody,
} from 'containers/QuestionnairePage/NodeSettings/settings/utils/CarryForward';

import { ANSWER_NONE_OF_THE_ABOVE } from 'containers/QuestionnairePage/constants';
import {
  isConjointOrMaxDiffGroup,
  isLoopAndMergeGroup,
  isBipolarNode,
  isMatrixSingleOrMultipleNode,
  isMatrixOpenEndedType,
  isSliderNode,
} from 'utils/nodeTypes';

import {
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERIC_OPEN_ENDED,
  QUESTION_DESCRIPTION,
  QUESTION_NPS,
  QUESTION_RATING,
  CARRIED_VALUE_PLACEHOLDERS,
  NODE_TYPES_SUPPORT_TRANSLATE_OPEN_ENDED,
} from 'data/nodeTypes';

import { ANSWER_TYPE } from './constants';

const getMatrixAnswerRefId = (
  originalQuestions,
  questionRefId,
  answerRefId,
) => {
  const question = find(originalQuestions, ['refId', questionRefId]);
  const matrixAnswers = get(question, 'matrix.matrixAnswers', []);
  const matrixAnswer = find(matrixAnswers, ['answerRefId', answerRefId], {});
  return matrixAnswer.refId;
};

export const shouldTranslateAnswers = ({ questionType }) => {
  switch (questionType) {
    case QUESTION_OPEN_ENDED:
    case QUESTION_NUMERIC_OPEN_ENDED:
    case QUESTION_DESCRIPTION:
    case QUESTION_NPS:
    case QUESTION_RATING:
      return false;
    default:
      return true;
  }
};

const transformGroupForTranslations = (group, translatedGroup) => {
  const { attributes, attributeValues } = translatedGroup;

  return {
    ...group,
    attributes: group.attributes.map((attribute) => {
      const translatedAttribute =
        find(attributes, ['originalRefId', attribute.refId]) || {};
      const newAttribute = {
        ...attribute,
        name: translatedAttribute.name || '',
        placeholder: attribute.name.trim(),
        isGoogleTranslated: translatedAttribute.isGoogleTranslated,
        translatedRefId: translatedAttribute.refId,
        values: attribute.values.map((attributeValue) => {
          const translatedAttributeValue =
            find(attributeValues, ['originalRefId', attributeValue.refId]) ||
            {};

          return {
            ...attributeValue,
            placeholder: attributeValue.text.trim(),
            text: translatedAttributeValue.text || '',
            isGoogleTranslated: translatedAttributeValue.isGoogleTranslated,
            translatedRefId: translatedAttributeValue.refId,
          };
        }),
      };

      return newAttribute;
    }),
  };
};

export const transformQuestionForTranslations = (
  question,
  translatedQuestion,
  translateOpenEnded,
) => {
  const newQuestion = cloneDeep(question);

  newQuestion.htmlPrompt = translatedQuestion.htmlPrompt || '';
  newQuestion.prompt = isHtmlPromptBlank(newQuestion.htmlPrompt)
    ? ''
    : getPromptFromHtml(newQuestion.htmlPrompt);
  newQuestion.promptPlaceholder = replaceMentionedPromptsWithBlank(
    question.prompt,
  );
  newQuestion.originPrompt = question.prompt;
  newQuestion.isGoogleTranslated = Boolean(
    translatedQuestion.isGoogleTranslated,
  );
  newQuestion.hasPiping = hasPiping(question.prompt);
  newQuestion.translatedRefId = translatedQuestion.refId;

  const hasOther = hasOtherAnswer({
    questionType: question.type,
    answers: get(question, 'answers', []),
  });
  newQuestion.hasOther = hasOther;

  if (
    hasOther ||
    NODE_TYPES_SUPPORT_TRANSLATE_OPEN_ENDED.includes(question.type)
  ) {
    newQuestion.translateOpenEnded = !!translateOpenEnded[question.refId];
  }

  if (shouldTranslateAnswers({ questionType: newQuestion.type })) {
    const translatedAnswers = get(translatedQuestion, 'answers', []);
    const newAnswers = get(newQuestion, 'answers', []).map((answer) => {
      const translatedAnswer =
        find(translatedAnswers, ['originalRefId', answer.refId]) || {};
      const newAnswer = {
        ...answer,
        placeholder: getPromptFromHtml(answer.body),
        bodyOrigin: answer.body,
        body: translatedAnswer.body || '',
        isGoogleTranslated: Boolean(translatedAnswer.isGoogleTranslated),
        translatedRefId: translatedAnswer.refId,
        hasPiping: hasPiping(translatedAnswer.body),
      };
      return newAnswer;
    });

    newQuestion.answers = newAnswers;
  }
  if (isMatrixSingleOrMultipleNode(newQuestion)) {
    const translatedScorepoints = get(translatedQuestion, 'scorepoints', []);
    const newScorepoints = newQuestion.scorepoints.map((scorepoint) => {
      const translatedScorepoint =
        find(translatedScorepoints, ['originalRefId', scorepoint.refId]) || {};
      const newScorepoint = {
        ...scorepoint,
        placeholder: getPromptFromHtml(scorepoint.body),
        bodyOrigin: scorepoint.body,
        body: translatedScorepoint.body || '',
        isGoogleTranslated: Boolean(translatedScorepoint.isGoogleTranslated),
        translatedRefId: translatedScorepoint.refId,
        hasPiping: hasPiping(translatedScorepoint.body),
      };

      return newScorepoint;
    });

    newQuestion.scorepoints = newScorepoints;
  }
  if (isSliderNode(newQuestion)) {
    const translatedTicks = get(translatedQuestion, 'ticks', []);
    const newTicks = newQuestion.slider.ticks.map((tick) => {
      const translatedTick =
        find(translatedTicks, ['originalRefId', tick.refId]) || {};
      const newTick = {
        ...tick,
        placeholder: getPromptFromHtml(tick.tickLabel),
        bodyOrigin: tick.tickLabel,
        tickLabel: translatedTick.tickLabel || '',
        isGoogleTranslated: Boolean(translatedTick.isGoogleTranslated),
        translatedRefId: translatedTick.refId,
        hasPiping: hasPiping(translatedTick.tickLabel),
      };
      return newTick;
    });

    newQuestion.slider.ticks = newTicks;
  }
  if (isBipolarNode(newQuestion)) {
    const matrixAnswers = get(newQuestion, 'matrix.matrixAnswers', []);
    const translatedAnswers = get(translatedQuestion, 'answers', []);
    const newAnswers = newQuestion.answers.map((answer) => {
      const translatedAnswer = find(translatedAnswers, [
        'originalRefId',
        answer.refId,
      ]);
      const translatedMatrixAnswer = get(translatedAnswer, 'matrixAnswer', {});
      const originalMatrixAnswer = find(matrixAnswers, [
        'answerRefId',
        answer.refId,
      ]);

      return getTranslatedMatrixAnswer(
        answer,
        translatedAnswer,
        originalMatrixAnswer,
        translatedMatrixAnswer,
      );
    });
    newQuestion.answers = newAnswers;

    const matrixColumns = get(newQuestion, 'matrix.columns', []);
    const translatedColumns = get(translatedQuestion, 'matrixColumns', []);
    const newColumns = matrixColumns.map((column) => {
      const translatedColumn =
        find(translatedColumns, ['originalRefId', column.refId]) || {};

      return getTranslatedMatrixColumn(column, translatedColumn);
    });
    newQuestion.matrix.columns = newColumns;
  }
  if (isMatrixOpenEndedType(newQuestion.type)) {
    const matrixAnswers = get(newQuestion, 'matrix.matrixAnswers', []);
    const translatedAnswers = get(translatedQuestion, 'answers', []);
    const matrixColumns = get(newQuestion, 'matrix.columns', []);
    const translatedColumns = get(translatedQuestion, 'matrixColumns', []);

    const newAnswers = newQuestion.answers.map((answer) => {
      const translatedAnswer = find(translatedAnswers, [
        'originalRefId',
        answer.refId,
      ]);
      const translatedMatrixAnswer = get(translatedAnswer, 'matrixAnswer', {});
      const originalMatrixAnswer = find(matrixAnswers, [
        'answerRefId',
        answer.refId,
      ]);

      return getTranslatedMatrixAnswer(
        answer,
        translatedAnswer,
        originalMatrixAnswer,
        translatedMatrixAnswer,
      );
    });
    newQuestion.answers = newAnswers;

    const newColumns = matrixColumns.map((column) => {
      const translatedColumn =
        find(translatedColumns, ['originalRefId', column.refId]) || {};

      return getTranslatedMatrixColumn(column, translatedColumn);
    });
    newQuestion.matrix.columns = newColumns;
  }

  return newQuestion;
};

export function getTranslatedMatrixAnswer(
  answer = {},
  translatedAnswer = {},
  originalMatrixAnswer = {},
  translatedMatrixAnswer = {},
) {
  return {
    ...answer,
    isGoogleTranslated: Boolean(translatedAnswer.isGoogleTranslated),
    beforeLabelOrigin: originalMatrixAnswer.beforeLabel,
    afterLabelOrigin: originalMatrixAnswer.afterLabel,
    matrixAnswer: {
      ...translatedMatrixAnswer,
      beforePlaceholder:
        getPromptFromHtml(originalMatrixAnswer.beforeLabel) ||
        '(Empty Statement)',
      afterPlaceholder: getPromptFromHtml(originalMatrixAnswer.afterLabel),
      beforeLabel: translatedMatrixAnswer.beforeLabel || '',
      afterLabel: translatedMatrixAnswer.afterLabel || '',
      isGoogleTranslated: Boolean(translatedMatrixAnswer.isGoogleTranslated),
      translatedRefId: translatedMatrixAnswer.refId,
    },
  };
}

export function getTranslatedMatrixColumn(column = {}, translatedColumn = {}) {
  return {
    ...column,
    beforePlaceholder: getPromptFromHtml(column.beforeLabel),
    afterPlaceholder: getPromptFromHtml(column.afterLabel),
    beforeLabelOrigin: column.beforeLabel,
    afterLabelOrigin: column.afterLabel,
    beforeLabel: translatedColumn.beforeLabel || '',
    afterLabel: translatedColumn.afterLabel || '',
    isGoogleTranslated: Boolean(translatedColumn.isGoogleTranslated),
    translatedRefId: translatedColumn.refId,
  };
}

export const transformNodesForTranslations = (
  nodes = [],
  translations,
  selectedLanguage,
  translateOpenEnded,
) => {
  const languageTranslations = find(translations, ['lang', selectedLanguage]);
  const translatedQuestions = get(languageTranslations, 'questions', []);

  return nodes.map((node) => {
    if (isConjointOrMaxDiffGroup(node.type) || isLoopAndMergeGroup(node.type)) {
      const translatedGroups = get(languageTranslations, 'questionGroups', []);
      const translatedGroup =
        find(translatedGroups, ['refId', node.refId]) || {};

      let newGroup = cloneDeep(node);

      // conjoint has only one question
      newGroup.questions = transformNodesForTranslations(
        newGroup.questions,
        translations,
        selectedLanguage,
        translateOpenEnded,
      );

      if (isConjointOrMaxDiffGroup(node.type)) {
        // translate only the noneOfTheAbove answer
        newGroup.questions[0].answers = newGroup.questions[0].answers.filter(
          (answer) => answer.type === ANSWER_NONE_OF_THE_ABOVE,
        );
      }

      newGroup = transformGroupForTranslations(newGroup, translatedGroup);

      return newGroup;
    }

    if (node.questions) {
      const newGroup = cloneDeep(node);
      // conjoint has only one question
      newGroup.questions = transformNodesForTranslations(
        newGroup.questions,
        translations,
        selectedLanguage,
        translateOpenEnded,
      );
      return newGroup;
    }

    const translatedQuestion =
      find(translatedQuestions, ['originalRefId', node.refId]) || {};

    return transformQuestionForTranslations(
      node,
      translatedQuestion,
      translateOpenEnded,
    );
  });
};

const isHtmlPromptBlank = (htmlPrompt) =>
  htmlPrompt === '' || htmlPrompt === '<div></div>';

export const isEmptyHtmlPrompt = ({ htmlPrompt, promptPlaceholder }) =>
  promptPlaceholder && isHtmlPromptBlank(htmlPrompt);

const skippedCFValues = Object.values(CARRIED_VALUE_PLACEHOLDERS);
const isSkippedCFAnswerValue = (answer) => {
  if (answer.isCarriedForward && skippedCFValues.includes(answer.placeholder)) {
    return true;
  }

  return false;
};

export const getHtmlTextForTranslation = (item, answerType) => {
  switch (answerType) {
    case ANSWER_TYPE.BIPOLAR_BEFORE:
    case ANSWER_TYPE.MATRIX_ANSWER_BEFORE:
    case ANSWER_TYPE.MATRIX_COLUMN_BEFORE:
      return item.beforeLabelOrigin || '';
    case ANSWER_TYPE.BIPOLAR_AFTER:
      return item.afterLabelOrigin || '';
    default:
      return item.bodyOrigin || '';
  }
};

/**
 *
 * @param {Array} list
 * @param {String} attribute "tickLabel" | "body"
 * @param {String} questionRefId
 * @param {String|undefined} answerType "column" | "tick" | "row" | undefined
 * @param {Array} mappingLogic
 * @param {boolean} allowEmptyPlaceHolder
 */
const extractPrompts = ({
  list = [],
  attribute,
  placeholder = 'placeholder',
  questionRefId,
  answerType,
  mappingLogic,
  allowEmptyPlaceHolder = false,
}) => {
  const newMappingLogic = [...mappingLogic];
  // TODO: filter carryForward answers, here and map in google translate success
  // .filter((item) => !item.isCarriedForward)
  list.forEach((item) => {
    const attributeValue = get(item, attribute);
    const placeholderValue = get(item, placeholder);
    const hasNoAttributeValue =
      !attributeValue || isHtmlPromptBlank(attributeValue);

    if (
      hasNoAttributeValue &&
      (placeholderValue || allowEmptyPlaceHolder) &&
      !isSkippedCFAnswerValue(item)
    ) {
      newMappingLogic.push({
        questionRefId,
        answerRefId: item.refId,
        text: wrapMentionsWithSpan(getHtmlTextForTranslation(item, answerType)),
        answerType,
      });
    }
  });

  return newMappingLogic;
};

const appendNotTranslatedPromptsForAnswers = (translatedNode, mappingLogic) => {
  const isASlider = isSliderNode(translatedNode);
  let newMappingLogic = [...mappingLogic];
  if (
    isASlider ||
    shouldTranslateAnswers({ questionType: translatedNode.type })
  ) {
    if (isASlider) {
      newMappingLogic = extractPrompts({
        list: translatedNode.slider.ticks,
        attribute: 'tickLabel',
        questionRefId: translatedNode.refId,
        answerType: ANSWER_TYPE.TICK,
        mappingLogic: newMappingLogic,
      });
    } else if (isMatrixSingleOrMultipleNode(translatedNode)) {
      newMappingLogic = extractPrompts({
        list: translatedNode.answers,
        attribute: 'body',
        questionRefId: translatedNode.refId,
        answerType: ANSWER_TYPE.ROW,
        mappingLogic: newMappingLogic,
      });

      newMappingLogic = extractPrompts({
        list: translatedNode.scorepoints,
        attribute: 'body',
        questionRefId: translatedNode.refId,
        answerType: ANSWER_TYPE.COLUMN,
        mappingLogic: newMappingLogic,
      });
    } else if (isBipolarNode(translatedNode)) {
      newMappingLogic = extractPrompts({
        list: translatedNode.answers,
        attribute: 'matrixAnswer.beforeLabel',
        placeholder: 'matrixAnswer.beforePlaceholder',
        questionRefId: translatedNode.refId,
        answerType: ANSWER_TYPE.BIPOLAR_BEFORE,
        mappingLogic: newMappingLogic,
      });
      newMappingLogic = extractPrompts({
        list: translatedNode.answers,
        attribute: 'matrixAnswer.afterLabel',
        placeholder: 'matrixAnswer.afterPlaceholder',
        questionRefId: translatedNode.refId,
        answerType: ANSWER_TYPE.BIPOLAR_AFTER,
        mappingLogic: newMappingLogic,
      });
      newMappingLogic = extractPrompts({
        list: get(translatedNode, 'matrix.columns', []),
        attribute: 'beforeLabel',
        placeholder: 'beforePlaceholder',
        questionRefId: translatedNode.refId,
        answerType: ANSWER_TYPE.MATRIX_COLUMN_BEFORE,
        mappingLogic: newMappingLogic,
        allowEmptyPlaceHolder: true,
      });
    } else if (isMatrixOpenEndedType(translatedNode.type)) {
      newMappingLogic = extractPrompts({
        list: translatedNode.answers,
        attribute: 'matrixAnswer.beforeLabel',
        placeholder: 'matrixAnswer.beforePlaceholder',
        questionRefId: translatedNode.refId,
        answerType: ANSWER_TYPE.MATRIX_ANSWER_BEFORE,
        mappingLogic: newMappingLogic,
      });
      newMappingLogic = extractPrompts({
        list: get(translatedNode, 'matrix.columns', []),
        attribute: 'beforeLabel',
        placeholder: 'beforePlaceholder',
        questionRefId: translatedNode.refId,
        answerType: ANSWER_TYPE.MATRIX_COLUMN_BEFORE,
        mappingLogic: newMappingLogic,
      });
    } else {
      newMappingLogic = extractPrompts({
        list: translatedNode.answers,
        attribute: 'body',
        questionRefId: translatedNode.refId,
        mappingLogic: newMappingLogic,
      });
    }
  }

  return newMappingLogic;
};

export const getNotTranslatedHtmlPromptsLogic = (
  allNodes,
  allTranslatedNodes,
) => {
  // mappingLogic maps the index of array to the specific refId of question or answer
  let mappingLogic = [];

  allTranslatedNodes.forEach((translatedNode) => {
    const originNode = allNodes.find(
      (node) => node.refId === translatedNode.refId,
    );

    if (isConjointOrMaxDiffGroup(translatedNode.type)) {
      mappingLogic = getNotTranslatedHtmlPromptsForConjointOrMaxDiff(
        originNode,
        translatedNode,
        mappingLogic,
      );
      return;
    }

    if (isLoopAndMergeGroup(translatedNode.type)) {
      mappingLogic = getNotTranslatedHtmlPromptsForLoopAndMergeVariables(
        originNode,
        translatedNode,
        mappingLogic,
      );
      return;
    }

    if (isEmptyHtmlPrompt(translatedNode)) {
      const { htmlPrompt, prompt } = originNode;

      // https://pollfish.atlassian.net/browse/RES-6320
      let textToTranslate;
      if (!isUndefined(htmlPrompt)) {
        textToTranslate = wrapMentionsWithSpan(htmlPrompt);
      } else if (!isUndefined(prompt)) {
        textToTranslate = wrapMentionsWithSpan(prompt);
      } else {
        textToTranslate = '';
      }

      mappingLogic.push({
        questionRefId: translatedNode.refId,
        text: textToTranslate,
      });
    }

    mappingLogic = appendNotTranslatedPromptsForAnswers(
      translatedNode,
      mappingLogic,
    );
  });

  return mappingLogic;
};

export const getNotTranslatedHtmlPromptsForConjointOrMaxDiff = (
  originGroupNode,
  translatedGroupNode,
  mappingLogic,
) => {
  let newMappingLogic = [...mappingLogic];

  const { questions, attributes: translatedAttributes } = translatedGroupNode;

  const [translatedNode] = questions;

  if (isEmptyHtmlPrompt(translatedNode)) {
    const { htmlPrompt } = originGroupNode.questions[0];

    newMappingLogic.push({
      questionRefId: translatedNode.refId,
      text: htmlPrompt,
    });
  }

  translatedAttributes.forEach((attribute) => {
    if (isEmpty(attribute.name)) {
      newMappingLogic.push({
        attributeRefId: attribute.refId,
        groupRefId: originGroupNode.refId,
        text: attribute.placeholder,
      });
    }

    attribute.values.forEach((attributeValue) => {
      if (isEmpty(attributeValue.text)) {
        newMappingLogic.push({
          attributeValueRefId: attributeValue.refId,
          groupRefId: originGroupNode.refId,
          text: attributeValue.placeholder,
        });
      }
    });
  });

  // noneOfTheAboveAnswer is rendered after attributes
  newMappingLogic = appendNotTranslatedPromptsForAnswers(
    translatedNode,
    newMappingLogic,
  );

  return newMappingLogic;
};

export const getNotTranslatedHtmlPromptsForLoopAndMergeVariables = (
  originGroupNode,
  translatedGroupNode,
  mappingLogic,
) => {
  const newMappingLogic = [...mappingLogic];

  const { attributes: translatedAttributes } = translatedGroupNode;

  translatedAttributes.forEach((attribute) => {
    if (!isLoopAndMergeAttributeBasedOnQuestion(attribute)) {
      attribute.values.forEach((attributeValue) => {
        if (isHtmlPromptBlank(attributeValue.text)) {
          newMappingLogic.push({
            attributeValueRefId: attributeValue.refId,
            groupRefId: originGroupNode.refId,
            text: attributeValue.placeholder,
          });
        }
      });
    }
  });

  return newMappingLogic;
};

function updateLoopAndMergeGroupsWithMergedNode(
  groupWithLoopAndMergeNode,
  {
    answerRefId,
    questionRefId,
    body,
    originalQuestions,
    translatedQuestions,
    translatedGroups,
    isGoogleTranslated = false,
  },
) {
  const groups = translatedGroups;
  const foundGroup = groups.find(
    (group) => group.refId === groupWithLoopAndMergeNode.refId,
  );

  if (!foundGroup) {
    groups.push({
      refId: groupWithLoopAndMergeNode.refId,
      attributeValues: [],
    });
  }

  return groups.map((group) => {
    if (group.refId === groupWithLoopAndMergeNode.refId) {
      const loopAndMergeConfig =
        groupWithLoopAndMergeNode.attributeSet.loopAndMerge;
      let { attributeValues } = group;
      const sourceQuestion = originalQuestions.find(
        (question) => question.refId === questionRefId,
      );
      const indexToUpdate = loopAndMergeConfig.groupingRelations.findIndex(
        (relation) => relation.originalAnswerOrScorepointRefId === answerRefId,
      );

      let bipolarBody;
      if (isBipolarNode(sourceQuestion)) {
        const sourceTranslatedQuestion = translatedQuestions.find(
          (question) => question.originalRefId === questionRefId,
        );
        const { matrixAnswer } = sourceTranslatedQuestion.answers.find(
          (answer) => answer.originalRefId === answerRefId,
        );

        bipolarBody = getBipolarAnswerBody(
          matrixAnswer,
          loopAndMergeConfig.answerLabelSelection,
        );
      }

      if (indexToUpdate !== -1) {
        const originalMergedAttribute =
          groupWithLoopAndMergeNode.attributeSet.attributes.find(
            isLoopAndMergeAttributeBasedOnQuestion,
          );
        const originalAttributeValueRefId =
          originalMergedAttribute.values[indexToUpdate].refId;

        attributeValues = unionBy(
          [
            {
              refId: uuid(),
              originalRefId: originalAttributeValueRefId,
              text: bipolarBody || body,
              isGoogleTranslated,
            },
          ],
          attributeValues,
          'originalRefId',
        );
      }

      return {
        ...group,
        attributeValues,
      };
    }

    return group;
  });
}

export function onUpdateAnswerUpdateLoopAndMergeGroups(properties) {
  const loopAndMergeGroupWithMergedNode = getLoopAndMergeGroupWithMergedNode(
    properties.questionRefId,
    properties.originalQuestionGroups,
  );

  if (loopAndMergeGroupWithMergedNode) {
    return updateLoopAndMergeGroupsWithMergedNode(
      loopAndMergeGroupWithMergedNode,
      properties,
    );
  }

  return properties.translatedGroups;
}

export function onGoogleTranslateUpdateLoopAndMergeGroupsWithMergedNode(
  translatedQuestions,
  translatedGroups,
  originalQuestions,
  originalQuestionGroups,
) {
  let newGroups = translatedGroups;

  translatedQuestions.forEach((node) => {
    const loopAndMergeGroupWithMergedNode = getLoopAndMergeGroupWithMergedNode(
      node.originalRefId,
      originalQuestionGroups,
    );

    if (loopAndMergeGroupWithMergedNode) {
      const loopAndMergeConfig =
        loopAndMergeGroupWithMergedNode.attributeSet.loopAndMerge;
      const {
        answers = [],
        scorepoints = [],
        matrixColumns = [],
        ticks = [],
      } = node;
      const list = [...answers, ...scorepoints, ...matrixColumns, ...ticks];

      loopAndMergeConfig.groupingRelations.forEach((relation) => {
        const translatedItem = list.find(
          (listItem) =>
            listItem.originalRefId === relation.originalAnswerOrScorepointRefId,
        );

        if (translatedItem) {
          newGroups = updateLoopAndMergeGroupsWithMergedNode(
            loopAndMergeGroupWithMergedNode,
            {
              questionRefId: node.originalRefId,
              answerRefId: translatedItem.originalRefId,
              body: translatedItem.body,
              originalQuestions,
              translatedQuestions,
              translatedGroups: newGroups,
              isGoogleTranslated: true,
            },
          );
        }
      });
    }
  });

  return newGroups;
}

/**
 *
 * @param {Array} translatedQuestions
 * @param {Array} originalQuestions
 * @param {String} questionRefId
 * @param {String|undefined} answerType "column" | "tick" | "row" | "bipolarBefore" | "bipolarAfter" | "bipolarBefore" | "bipolarAfter" | "matrixAnswerBefore" | "matrixColumnBefore" | undefined
 * @param {String} answerRefId
 * @param {String} body
 * @param {Boolean|undefined} isGoogleTranslated
 */
export const onUpdateAnswer = (
  translatedQuestions,
  originalQuestions,
  questionRefId,
  answerType,
  answerRefId,
  body,
  isGoogleTranslated,
) => {
  let foundQuestion = false;
  const newQuestions = translatedQuestions.map((translateQuestion) => {
    if (translateQuestion.originalRefId === questionRefId) {
      foundQuestion = true;

      switch (answerType) {
        case ANSWER_TYPE.COLUMN: {
          let foundScorepoint = false;
          const newScorepoints = translateQuestion.scorepoints
            ? translateQuestion.scorepoints.map((scorepoint) => {
                if (scorepoint.originalRefId === answerRefId) {
                  foundScorepoint = true;
                  return {
                    ...scorepoint,
                    body,
                    isGoogleTranslated: !!isGoogleTranslated,
                  };
                }
                return scorepoint;
              })
            : [];

          if (!foundScorepoint) {
            newScorepoints.push({
              originalRefId: answerRefId,
              refId: uuid(),
              body,
              isGoogleTranslated: !!isGoogleTranslated,
            });
          }
          return {
            ...translateQuestion,
            scorepoints: newScorepoints,
          };
        }
        case ANSWER_TYPE.TICK: {
          let foundTick = false;
          const newTicks = translateQuestion.ticks
            ? translateQuestion.ticks.map((tick) => {
                if (tick.originalRefId === answerRefId) {
                  foundTick = true;

                  return {
                    ...tick,
                    tickLabel: body,
                    isGoogleTranslated: !!isGoogleTranslated,
                  };
                }
                return tick;
              })
            : [];

          if (!foundTick) {
            newTicks.push({
              originalRefId: answerRefId,
              refId: uuid(),
              tickLabel: body,
              isGoogleTranslated: !!isGoogleTranslated,
            });
          }

          return {
            ...translateQuestion,
            ticks: newTicks,
          };
        }
        case ANSWER_TYPE.BIPOLAR_BEFORE:
        case ANSWER_TYPE.BIPOLAR_AFTER: {
          let foundAnswer = false;
          const labelName =
            answerType === ANSWER_TYPE.BIPOLAR_BEFORE
              ? 'beforeLabel'
              : 'afterLabel';
          const newAnswers = translateQuestion.answers
            ? translateQuestion.answers.map((answer) => {
                if (answer.originalRefId === answerRefId) {
                  foundAnswer = true;
                  const { matrixAnswer = {} } = answer;

                  return {
                    ...answer,
                    isGoogleTranslated: !!isGoogleTranslated,
                    matrixAnswer: {
                      ...matrixAnswer,
                      [labelName]: body,
                      isGoogleTranslated: !!isGoogleTranslated,
                    },
                  };
                }
                return answer;
              })
            : [];

          if (!foundAnswer) {
            const matrixAnswerRefId = getMatrixAnswerRefId(
              originalQuestions,
              questionRefId,
              answerRefId,
            );
            const newRefId = uuid();
            newAnswers.push({
              originalRefId: answerRefId,
              refId: newRefId,
              body: '',
              isGoogleTranslated: !!isGoogleTranslated,
              matrixAnswer: {
                refId: uuid(),
                answerRefId: newRefId,
                originalRefId: matrixAnswerRefId,
                [labelName]: body,
                isGoogleTranslated: !!isGoogleTranslated,
              },
            });
          }

          return {
            ...translateQuestion,
            answers: newAnswers,
          };
        }
        case ANSWER_TYPE.MATRIX_ANSWER_BEFORE: {
          let foundAnswer = false;

          const newAnswers = translateQuestion.answers
            ? translateQuestion.answers.map((answer) => {
                if (answer.originalRefId === answerRefId) {
                  foundAnswer = true;
                  const { matrixAnswer = {} } = answer;

                  return {
                    ...answer,
                    isGoogleTranslated: !!isGoogleTranslated,
                    matrixAnswer: {
                      ...matrixAnswer,
                      beforeLabel: body,
                      isGoogleTranslated: !!isGoogleTranslated,
                    },
                  };
                }
                return answer;
              })
            : [];

          if (!foundAnswer) {
            const matrixAnswerRefId = getMatrixAnswerRefId(
              originalQuestions,
              questionRefId,
              answerRefId,
            );
            const newRefId = uuid();
            newAnswers.push({
              originalRefId: answerRefId,
              refId: newRefId,
              body: '',
              isGoogleTranslated: !!isGoogleTranslated,
              matrixAnswer: {
                refId: uuid(),
                answerRefId: newRefId,
                originalRefId: matrixAnswerRefId,
                beforeLabel: body,
                isGoogleTranslated: !!isGoogleTranslated,
              },
            });
          }

          return {
            ...translateQuestion,
            answers: newAnswers,
          };
        }
        case ANSWER_TYPE.MATRIX_COLUMN_BEFORE: {
          let foundColumn = false;
          const matrixColumns = get(translateQuestion, 'matrixColumns', []);
          const newMatrixColumns = matrixColumns.map((column) => {
            if (column.originalRefId === answerRefId) {
              foundColumn = true;
              return {
                ...column,
                isGoogleTranslated: !!isGoogleTranslated,
                beforeLabel: body,
              };
            }
            return column;
          });

          if (!foundColumn) {
            const newRefId = uuid();
            newMatrixColumns.push({
              originalRefId: answerRefId,
              refId: newRefId,
              beforeLabel: body,
              isGoogleTranslated: !!isGoogleTranslated,
            });
          }

          return {
            ...translateQuestion,
            matrixColumns: newMatrixColumns,
          };
        }
        case ANSWER_TYPE.ROW:
        default: {
          let foundAnswer = false;
          const newAnswers = translateQuestion.answers
            ? translateQuestion.answers.map((answer) => {
                if (answer.originalRefId === answerRefId) {
                  foundAnswer = true;

                  return {
                    ...answer,
                    body,
                    isGoogleTranslated: !!isGoogleTranslated,
                  };
                }
                return answer;
              })
            : [];

          if (!foundAnswer) {
            newAnswers.push({
              originalRefId: answerRefId,
              refId: uuid(),
              body,
              isGoogleTranslated: !!isGoogleTranslated,
            });
          }

          return {
            ...translateQuestion,
            answers: newAnswers,
          };
        }
      }
    }
    return translateQuestion;
  });

  if (!foundQuestion) {
    const newQuestion = {
      originalRefId: questionRefId,
      refId: uuid(),
      prompt: '',
      htmlPrompt: '',
      isGoogleTranslated: false,
    };
    const newAnswerItem = {
      originalRefId: answerRefId,
      refId: uuid(),
      body,
      isGoogleTranslated: !!isGoogleTranslated,
    };

    switch (answerType) {
      case ANSWER_TYPE.COLUMN: {
        newQuestion.answers = [];
        newQuestion.scorepoints = [newAnswerItem];
        break;
      }
      case ANSWER_TYPE.TICK: {
        newQuestion.ticks = [
          { ...newAnswerItem, tickLabel: body, body: undefined },
        ];
        break;
      }
      case ANSWER_TYPE.BIPOLAR_BEFORE:
      case ANSWER_TYPE.BIPOLAR_AFTER:
        {
          const matrixAnswerRefId = getMatrixAnswerRefId(
            originalQuestions,
            questionRefId,
            answerRefId,
          );
          const matrixAnswer = {
            beforeLabel: '',
            afterLabel: '',
            refId: uuid(),
            answerRefId: newAnswerItem.refId,
            originalRefId: matrixAnswerRefId,
            isGoogleTranslated: !!isGoogleTranslated,
          };
          newQuestion.answers = [{ ...newAnswerItem, matrixAnswer }];
        }
        break;
      case ANSWER_TYPE.MATRIX_ANSWER_BEFORE:
        {
          const matrixAnswerRefId = getMatrixAnswerRefId(
            originalQuestions,
            questionRefId,
            answerRefId,
          );
          const matrixAnswer = {
            beforeLabel: '',
            afterLabel: '',
            refId: uuid(),
            answerRefId: newAnswerItem.refId,
            originalRefId: matrixAnswerRefId,
            isGoogleTranslated: !!isGoogleTranslated,
          };
          newQuestion.answers = [{ ...newAnswerItem, matrixAnswer }];
        }
        break;
      case ANSWER_TYPE.MATRIX_COLUMN_BEFORE:
        {
          const matrixColumn = {
            beforeLabel: '',
            afterLabel: '',
          };
          newQuestion.matrixColumns = [{ ...newAnswerItem, ...matrixColumn }];
        }
        break;
      case ANSWER_TYPE.ROW:
      default: {
        newQuestion.answers = [newAnswerItem];
        break;
      }
    }

    newQuestions.push(newQuestion);
  }

  return newQuestions;
};

/**
 *
 * @param {Array} questions
 * @param {String} refId
 * @param {Object} attributes
 * @param {String|undefined} attributes.prompt
 * @param {String|undefined} attributes.htmlPrompt
 * @param {Boolean|undefined} attributes.isGoogleTranslated
 * @param {Boolean|undefined} attributes.translateOpenEnded
 * @param {Object|undefined} batchAnswers
 */
export const onUpdateQuestion = (
  translatedQuestions,
  refId,
  attributes,
  batchAnswers,
) => {
  let foundQuestion = false;

  const newQuestions = translatedQuestions.map((translatedQuestion) => {
    if (translatedQuestion.originalRefId === refId) {
      foundQuestion = true;
      return isUndefined(batchAnswers)
        ? {
            ...translatedQuestion,
            ...attributes,
          }
        : {
            ...translatedQuestion,
            ...batchAnswers,
          };
    }
    return translatedQuestion;
  });

  if (!foundQuestion) {
    newQuestions.push(
      isUndefined(batchAnswers)
        ? {
            originalRefId: refId,
            refId: uuid(),
            ...attributes,
            answers: [],
          }
        : {
            originalRefId: refId,
            refId: uuid(),
            ...attributes,
            prompt: '',
            htmlPrompt: '',
            isGoogleTranslated: attributes.isGoogleTranslated,
            ...batchAnswers,
          },
    );
  }

  return newQuestions;
};

const createNewAttributeOrAttributeValue = (originalRefId, rest) => ({
  originalRefId,
  refId: uuid(),
  ...rest,
});

export const onUpdateGroup = ({
  groups,
  groupRefId,
  itemRefId,
  itemType,
  rest,
}) => {
  let foundGroup = false;
  const newGroups = groups.map((group) => {
    if (group.refId === groupRefId) {
      foundGroup = true;

      const attributesOrAttributeValues = group[itemType];

      if (!attributesOrAttributeValues) {
        return {
          ...group,
          [itemType]: [createNewAttributeOrAttributeValue(itemRefId, rest)],
        };
      }

      let foundItem = false;
      const newAttributesOrAttributeValues = attributesOrAttributeValues.map(
        (attributeOrAttributeValue) => {
          if (attributeOrAttributeValue.originalRefId === itemRefId) {
            foundItem = true;

            return {
              ...attributeOrAttributeValue,
              ...rest,
            };
          }

          return attributeOrAttributeValue;
        },
      );

      if (!foundItem) {
        newAttributesOrAttributeValues.push(
          createNewAttributeOrAttributeValue(itemRefId, rest),
        );
      }

      return {
        ...group,
        [itemType]: newAttributesOrAttributeValues,
      };
    }

    return group;
  });

  if (!foundGroup) {
    newGroups.push({
      refId: groupRefId,
      [itemType]: [createNewAttributeOrAttributeValue(itemRefId, rest)],
    });
  }

  return newGroups;
};

export const htmlDecode = (text) =>
  new DOMParser().parseFromString(text, 'text/html').body.innerHTML;

export const removeLanguageFromGoogleTranslated = (
  googleTranslatedLanguages,
  language,
) => googleTranslatedLanguages.filter((lang) => lang !== language);

export const addLanguageToGoogleTranslated = (
  googleTranslatedLanguages,
  language,
) =>
  googleTranslatedLanguages.includes(language)
    ? googleTranslatedLanguages
    : [...googleTranslatedLanguages, language];

export const flatMapNodes = (nodes) =>
  nodes.flatMap((node) => {
    if (isConjointOrMaxDiffGroup(node.type) || !node.questions) {
      return node;
    }

    if (isLoopAndMergeGroup(node.type)) {
      return [{ ...node, questions: undefined }, ...node.questions];
    }

    return node.questions;
  });

export const checkOpenEndedOrOtherDeep = (nodes) =>
  nodes.some((node) => {
    if (node.questions) {
      return checkOpenEndedOrOtherDeep(node.questions);
    }
    return (
      NODE_TYPES_SUPPORT_TRANSLATE_OPEN_ENDED.includes(node.type) ||
      node.hasOther
    );
  });

export const isSomeTranslateOpenEndedFalse = (nodes) =>
  nodes.some((node) => {
    if (node.questions) {
      return isSomeTranslateOpenEndedFalse(node.questions);
    }
    return node.translateOpenEnded === false;
  });

export const extractRefIdsThatHaveTranslateOpenEnded = (nodes) => {
  const toRefIdIfHasTranslateOpenEndedDeep = (node) => {
    if (isBoolean(node.translateOpenEnded)) {
      return node.refId;
    }

    if (node.questions) {
      return node.questions.map(toRefIdIfHasTranslateOpenEndedDeep);
    }

    return [];
  };

  return flatMapDeep(nodes, toRefIdIfHasTranslateOpenEndedDeep);
};

export const fillInDummyBipolarColumns = ({
  originalQuestions,
  translation,
  questionRefId,
}) => {
  const originalQuestion = originalQuestions.find(
    (question) => question.refId === questionRefId,
  );

  if (isBipolarNode(originalQuestion)) {
    const updatedTranslations = cloneDeep(translation);
    const matrixColumns = get(originalQuestion, 'matrix.columns', []);
    matrixColumns.forEach((column) => {
      updatedTranslations.questions = onUpdateAnswer(
        updatedTranslations.questions,
        originalQuestions,
        questionRefId,
        'matrixColumnBefore',
        column.refId,
        '',
      );
    });
    return updatedTranslations.questions;
  }

  return translation.questions;
};
